import { checkSession } from "@features/auth";
import { getUserOrganizationId } from "@features/auth/useAuth";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { preloadQuery } from "@relay";
import { Badge } from "@shadcn/ui/badge";
import { Button } from "@shadcn/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@shadcn/ui/tabs";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";
import { usePreloadedQuery } from "react-relay";
import { graphql } from "relay-runtime";
import { toast } from "sonner";
import {
	formatConfigurationLabel,
	formatDimensions,
	getSizeBucket,
	SIZE_BUCKET_ORDER,
	type SizeBucket,
} from "~/components/lock/spaceConfiguration";
import { AppHeader } from "~/components/layout/AppHeader";
import { PageContainer } from "~/components/layout/PageContainer";
import type { selectMyLocksQuery } from "./__generated__/selectMyLocksQuery.graphql";

export const query = graphql`
	query selectMyLocksQuery($hubName: String!) @relay_test_operation {
		hubMeta(by: { name: $hubName }) @required(action: THROW) {
			id
			name
			hub @required(action: THROW) {
				id
				status
				availableConfigurations {
					numSpaces
					configuration
					dimensions {
						widthMm
						heightMm
						depthMm
					}
					spaces {
						id
					}
				}
			}
		}
	}
`;

export const Route = createFileRoute("/$hubMetaName/swap/select")({
	beforeLoad: () => {
		document.title = "bacehub | Lock";
	},
	component: RouteComponent,
	loader: async (ctx) => {
		const sessionExists = await checkSession();
		if (!sessionExists) {
			throw redirect({
				to: "/auth",
				search: { redirectToPath: `/${ctx.params.hubMetaName}/swap/select${ctx.location.searchStr}` },
			});
		}
		if (!getUserOrganizationId()) {
			throw redirect({ to: "/" });
		}

		// Multiple rentals are allowed: from here the user can always rent another
		// compartment, even if they already hold an active lock at this hub.
		return preloadQuery<selectMyLocksQuery>(ctx.context.relayEnvironment, query, {
			hubName: ctx.params.hubMetaName,
		});
	},
});

function RouteComponent() {
	const navigate = useNavigate();
	const i18n = useI18nContext().LL["new-ui"];
	const { hubMeta } = usePreloadedQuery<selectMyLocksQuery>(query, Route.useLoaderData());
	const isHubOnline = hubMeta.hub.status === "ONLINE";

	// Only configurations that have at least one free space can be rented.
	const configurations = hubMeta.hub.availableConfigurations.filter((config) => config.numSpaces > 0 && config.spaces.length > 0);

	// Size buckets actually available, in fixed S/M/L/XL order, for filter tabs.
	const availableSizes = SIZE_BUCKET_ORDER.filter((size) => configurations.some((config) => getSizeBucket(config.configuration) === size));
	const [sizeFilter, setSizeFilter] = useState<SizeBucket | undefined>(availableSizes[0]);

	const visibleConfigurations = sizeFilter
		? configurations.filter((config) => getSizeBucket(config.configuration) === sizeFilter)
		: configurations;

	// Renting no longer happens here: the reservation is created at the summary
	// step (where the user confirms). We only carry the chosen space forward.
	const handleChoose = (config: (typeof configurations)[number]) => {
		const spaceId = config.spaces[0]?.id;
		if (!spaceId) {
			toast.error(i18n.SOMETHING_WENT_WRONG());
			return;
		}
		navigate({
			to: "/$hubMetaName/swap/summary/$spaceId",
			params: { hubMetaName: hubMeta.name, spaceId },
		});
	};

	if (configurations.length === 0) {
		return (
			<PageContainer>
				<AppHeader hubMetaName={hubMeta.name} service="LOCK_B2B" />
				<div className="flex flex-col gap-4 px-6 pt-6">
					<div className="flex flex-col gap-2">
						<h1 className="text-2xl font-breit font-medium">
							<T>{i18n.LOCK_RENT_TITLE()}</T>
						</h1>
						<p className="text-sm text-muted-foreground">
							<T>{i18n.LOCK_RENT_EMPTY()}</T>
						</p>
					</div>
					<Button
						variant="outline"
						className="w-full"
						onClick={() => navigate({ to: "/$hubMetaName/swap/my", params: { hubMetaName: hubMeta.name } })}
					>
						<ArrowLeft className="size-4" />
						<T>{i18n.LOCK_RENT_EMPTY_BACK()}</T>
					</Button>
				</div>
			</PageContainer>
		);
	}

	return (
		<PageContainer>
			<AppHeader hubMetaName={hubMeta.name} service="LOCK_B2B" />
			<div className="flex flex-col gap-6 px-6 pt-6">
				<h1 className="text-2xl font-breit font-medium">
					<T>{i18n.LOCK_RENT_TITLE()}</T>
				</h1>
				{!isHubOnline ? (
					<p role="status" className="rounded-lg bg-muted px-4 py-3 text-sm text-muted-foreground">
						<T>{i18n.LOCK_RENT_HUB_OFFLINE()}</T>
					</p>
				) : null}

				{/* Keep all size categories visible so availability is clear at a glance. */}
				{availableSizes.length > 0 ? (
					<Tabs value={sizeFilter} onValueChange={(value) => setSizeFilter(value as SizeBucket)}>
						<TabsList className="grid h-11 w-full grid-cols-5">
							{SIZE_BUCKET_ORDER.map((size) => {
								const count = configurations
									.filter((config) => getSizeBucket(config.configuration) === size)
									.reduce((total, config) => total + config.numSpaces, 0);
								return (
									<TabsTrigger key={size} value={size} disabled={count === 0}>
										{size}
										<span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-foreground px-1.5 text-xs font-semibold text-background">
											{count}
										</span>
									</TabsTrigger>
								);
							})}
						</TabsList>
					</Tabs>
				) : null}

				<div className="flex flex-col gap-4">
					{visibleConfigurations.map((config) => (
						<div
							key={config.configuration}
							className={`relative flex flex-col gap-4 rounded-[14px] border bg-background p-6 shadow-md ${isHubOnline ? "" : "opacity-50"}`}
						>
							<div className="flex items-start justify-between gap-2">
								<div className="flex min-w-0 flex-col gap-1">
									<p className="text-xl font-breit font-medium leading-none">{formatConfigurationLabel(config.configuration)}</p>
									<p className="text-base text-muted-foreground">{formatDimensions(config.dimensions)}</p>
								</div>
								<Badge variant="secondary" className="shrink-0">
									{i18n.LOCK_RENT_AVAILABLE({ count: config.numSpaces })}
								</Badge>
							</div>
							<Button className="w-full" disabled={!isHubOnline} onClick={() => handleChoose(config)}>
								<T>{i18n.LOCK_RENT_CHOOSE()}</T>
							</Button>
						</div>
					))}
				</div>
			</div>
		</PageContainer>
	);
}
